import React from 'react';
import { createPortal } from 'react-dom';
import { X, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';

const MIN_SCALE = 1;
const MAX_SCALE = 8;
const clamp = (s: number) => Math.max(MIN_SCALE, Math.min(MAX_SCALE, s));

interface Props {
  src: string;
  alt?: string;
  onClose: () => void;
}

/** Full-screen photo viewer: opens fitted to the screen, wheel / pinch zooms
 *  toward the pointer, drag pans, double-click toggles fit ↔ 2.5×, Esc closes. */
const ImageZoomModal: React.FC<Props> = ({ src, alt = '', onClose }) => {
  const stageRef = React.useRef<HTMLDivElement>(null);
  const [view, setView] = React.useState({ scale: 1, x: 0, y: 0 });
  const pointers = React.useRef(new Map<number, { x: number; y: number }>());
  const pinchDist = React.useRef(0);
  const moved = React.useRef(false);

  const fit = React.useCallback(() => setView({ scale: 1, x: 0, y: 0 }), []);
  React.useEffect(fit, [src, fit]);

  // Zoom keeping the stage point (cx, cy) — client coords — under the pointer.
  const zoomAt = React.useCallback((factor: number, cx?: number, cy?: number) => {
    const rect = stageRef.current?.getBoundingClientRect();
    setView(v => {
      const scale = clamp(v.scale * factor);
      if (scale === MIN_SCALE) return { scale, x: 0, y: 0 };
      if (!rect) return { ...v, scale };
      const px = (cx ?? rect.left + rect.width / 2) - (rect.left + rect.width / 2);
      const py = (cy ?? rect.top + rect.height / 2) - (rect.top + rect.height / 2);
      const k = scale / v.scale;
      return { scale, x: px - (px - v.x) * k, y: py - (py - v.y) * k };
    });
  }, []);

  // Native non-passive wheel listener — React's onWheel can't preventDefault.
  React.useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      zoomAt(Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0025)), e.clientX, e.clientY);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomAt]);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === '+' || e.key === '=') zoomAt(1.4);
      else if (e.key === '-') zoomAt(1 / 1.4);
      else if (e.key === '0') fit();
    };
    window.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [onClose, zoomAt, fit]);

  const onPointerDown = (e: React.PointerEvent) => {
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    moved.current = false;
    if (pointers.current.size === 2) {
      const [a, b] = Array.from(pointers.current.values());
      pinchDist.current = Math.hypot(a.x - b.x, a.y - b.y);
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const prev = pointers.current.get(e.pointerId);
    if (!prev) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = Array.from(pointers.current.values());
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      if (pinchDist.current) zoomAt(dist / pinchDist.current, (a.x + b.x) / 2, (a.y + b.y) / 2);
      pinchDist.current = dist;
      moved.current = true;
      return;
    }
    const dx = e.clientX - prev.x;
    const dy = e.clientY - prev.y;
    if (!moved.current && Math.abs(dx) + Math.abs(dy) > 2) {
      moved.current = true;
      // Capture only once it's a drag — capturing on down retargets a plain
      // click on the photo to the backdrop, which would close the viewer.
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    setView(v => (v.scale > MIN_SCALE ? { ...v, x: v.x + dx, y: v.y + dy } : v));
  };

  const onPointerUp = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    pinchDist.current = 0;
  };

  const btn = 'text-white bg-black/60 hover:bg-black/80 rounded-full p-2 transition-colors';
  const zoomed = view.scale > MIN_SCALE;

  return createPortal(
    <div className="fixed inset-0 bg-black/90 select-none" style={{ zIndex: 100000 }} role="dialog" aria-modal="true">
      <div
        ref={stageRef}
        className="absolute inset-0 flex items-center justify-center overflow-hidden p-4 sm:p-10"
        style={{ touchAction: 'none', cursor: zoomed ? 'grab' : 'zoom-in' }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onClick={(e) => {
          // A click on the dark backdrop (not a drag, not the photo) closes.
          if (!moved.current && e.target === e.currentTarget) onClose();
        }}
        onDoubleClick={(e) => (zoomed ? fit() : zoomAt(2.5, e.clientX, e.clientY))}
      >
        <img
          src={src}
          alt={alt}
          draggable={false}
          className="max-w-full max-h-full object-contain rounded-lg"
          style={{
            transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
            transformOrigin: 'center center',
            willChange: 'transform',
          }}
        />
      </div>

      <button className={`${btn} absolute top-4 right-4`} onClick={onClose} title="Close (Esc)" aria-label="Close">
        <X className="w-6 h-6" />
      </button>
      <div className="absolute top-4 left-4 flex flex-col items-center gap-2">
        <button className={btn} onClick={() => zoomAt(1.4)} title="Zoom in (+)" aria-label="Zoom in">
          <ZoomIn className="w-5 h-5" />
        </button>
        <button className={btn} onClick={() => zoomAt(1 / 1.4)} title="Zoom out (−)" aria-label="Zoom out">
          <ZoomOut className="w-5 h-5" />
        </button>
        <button className={btn} onClick={fit} title="Fit to screen (0)" aria-label="Fit to screen">
          <Maximize2 className="w-5 h-5" />
        </button>
        <div className="text-white bg-black/60 rounded-full px-3 py-1 text-xs tabular-nums">
          {Math.round(view.scale * 100)}%
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ImageZoomModal;
